import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { useSelector } from 'react-redux';
import { selectGroupGuid, selectUserGuid } from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { useActualIncidentId } from './commonHooks';
import { MedicalTransportationBase } from '../../../types/iapBaseTypes';
import { IapScope, INCIDENT } from '../ICSConstants';

export type IncidentMedicalTransportation = {
  id: string;
  transportation_id: string;
  incident_id: string;
  user_guid: string;
  version: number;
  timestamp: Date;
  archived: boolean;
} & MedicalTransportationBase;

export const keys = {
  base: 'incident_medicalTransportation',
  get all() {
    return [keys.base];
  },
  list: (incidentId: string, userId: string) => [keys.base, incidentId, userId],
} as const;

export type CreateIncidentMedicalTransportationReqData = {
  incident_id: string;
  archived?: boolean;
} & MedicalTransportationBase;

export const useIncidentMedicalTransportations = (scope?: IapScope) => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.list(incidentId, user_guid),
    queryFn: async () => {
      const resp = await axios.get<IncidentMedicalTransportation[]>(
        `${config.apiGateway.ics_incident_medical_transportation}/${incidentId}`
      );
      return resp.data;
    },
    enabled: scope === INCIDENT,
  });
};

export const useCreateIncidentMedicalTransportation = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: CreateIncidentMedicalTransportationReqData) => {
      return await axios.post(
        `${config.apiGateway.ics_incident_medical_transportation}/${incidentId}`,
        { ...data, user_guid, group_guid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });
  return mutation;
};

export type EditIncidentMedicalTransportationReqData = {
  id: string;
  transportation_id: string;
  archived?: boolean;
} & MedicalTransportationBase;

export const useEditIncidentMedicalTransportation = () => {
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditIncidentMedicalTransportationReqData) => {
      const { id, ...updateData } = data;
      return await axios.put(
        `${config.apiGateway.ics_incident_medical_transportation}/${incidentId}/${id}`,
        { ...updateData, user_guid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });
};
